import { useState } from "react";
import { ChevronDown, ChevronUp, UserPlus } from "lucide-react";
import type { CategoryEntitlement, MemberScopeMode } from "../../context/auth-me";
import { Dropdown } from "../../components/ui/dropdown";
import type { OwnerInvitation } from "../settings/types";
import {
  ASSIGNABLE_ROLE_OPTIONS,
  draftToScope,
  FULL_SCOPE_DRAFT,
  ownerErrorCode,
  ownerErrorMessage,
  type AssignableRole,
  type StoredScope,
} from "./access-model";
import { AgentScopeEditor } from "./agent-scope-editor";
import { PermissionPicker } from "./permission-picker";

/**
 * org_invitations: invite with a role, inline grants and agent scope.
 *
 * The scope is always sent, defaulting to every agent: core resolves an unset member
 * scope to no agents once member_scope enforces, which would strand the invitee.
 */
export function InviteForm({ entitlements, memberScope, onInvite, onIssued, onResendPending }: {
  entitlements: Record<string, CategoryEntitlement> | null | undefined;
  memberScope: MemberScopeMode;
  onInvite: (
    email: string, role: AssignableRole, access: { permissions: string[]; resource_scope: StoredScope | null },
  ) => Promise<OwnerInvitation>;
  onIssued: (invite: OwnerInvitation) => void;
  /** Resend the pending invitation for this address, if the list has one. */
  onResendPending: (email: string) => Promise<boolean>;
}) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<AssignableRole>("member");
  const [permissions, setPermissions] = useState<string[]>([]);
  const [scope, setScope] = useState(FULL_SCOPE_DRAFT);
  const [showAccess, setShowAccess] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<{ message: string; pending: boolean } | null>(null);
  const valid = /.+@.+\..+/.test(email);

  const reset = () => {
    setEmail(""); setRole("member"); setPermissions([]); setScope(FULL_SCOPE_DRAFT); setShowAccess(false);
  };

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const invite = await onInvite(email, role, { permissions, resource_scope: draftToScope(scope) });
      onIssued(invite);
      reset();
    } catch (err) {
      setError({ message: ownerErrorMessage(err), pending: ownerErrorCode(err) === "invitation_pending" });
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setBusy(true);
    try {
      if (await onResendPending(email)) { setError(null); reset(); }
      else setError({ message: "Could not find that pending invitation. Refresh and try again.", pending: false });
    } catch (err) {
      setError({ message: ownerErrorMessage(err), pending: false });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4 rounded-xl border border-line bg-elevated p-4 shadow-sm">
      <div className="flex flex-wrap items-center gap-3">
        <input
          value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="teammate@company.com"
          aria-label="Invite email"
          className="w-72 rounded-lg border border-line px-3 py-2 text-sm text-ink focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/30"
        />
        <Dropdown ariaLabel="Invite role" className="w-36" value={role} options={ASSIGNABLE_ROLE_OPTIONS} onChange={setRole} />
        <button
          type="button"
          onClick={() => setShowAccess((open) => !open)}
          className="flex items-center gap-1 rounded-lg px-3 py-2 text-sm text-ink-2 hover:bg-soft"
        >
          Access {showAccess ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
        <button
          disabled={!valid || busy}
          onClick={() => { void submit(); }}
          className="flex items-center gap-2 rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-hover disabled:cursor-not-allowed disabled:bg-line disabled:text-ink-3"
        >
          <UserPlus className="h-4 w-4" /> Invite
        </button>
      </div>

      {error && (
        <div role="alert" className="flex flex-wrap items-center gap-3 rounded-lg bg-danger-soft p-3 text-sm text-danger">
          <span>{error.message}</span>
          {error.pending && (
            <button onClick={() => { void resend(); }} disabled={busy} className="font-semibold underline">
              Resend invitation
            </button>
          )}
        </div>
      )}

      {showAccess && (
        <div className="space-y-6 border-t border-line pt-4">
          <PermissionPicker role={role} value={permissions} onChange={setPermissions} entitlements={entitlements} />
          <div>
            <h4 className="mb-3 text-sm font-semibold text-ink">Agent access</h4>
            <AgentScopeEditor value={scope} onChange={setScope} enforced={memberScope} />
          </div>
        </div>
      )}
    </div>
  );
}
